import React from 'react';
import { Drawer, Select, Button, Row, Col, Typography, Divider, Space } from 'antd';
import { FilterOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import './FilterDrawer.css';

const { Option } = Select;
const { Title, Text } = Typography;

interface FilterDrawerProps {
  visible: boolean;
  onClose: () => void;
  selectedBU: string[];
  selectedClient: string[];
  selectedDepartment: string[];
  startYear: number;
  startQuarter: number;
  endYear: number;
  endQuarter: number;
  onBUChange: (value: string[]) => void;
  onClientChange: (value: string[]) => void;
  onDepartmentChange: (value: string[]) => void;
  onStartYearChange: (value: number) => void;
  onStartQuarterChange: (value: number) => void;
  onEndYearChange: (value: number) => void;
  onEndQuarterChange: (value: number) => void;
  onReset: () => void;
  onSearch: () => void;
  businessUnits?: string[];
  clients?: string[];
  departments?: string[];
  loading?: boolean;
  activeView?: 'finance' | 'sales';
  onViewChange?: (view: 'finance' | 'sales') => void;
  showViewSelector?: boolean;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  visible,
  onClose,
  selectedBU,
  selectedClient,
  selectedDepartment,
  startYear,
  startQuarter,
  endYear,
  endQuarter,
  onBUChange,
  onClientChange,
  onDepartmentChange,
  onStartYearChange,
  onStartQuarterChange,
  onEndYearChange,
  onEndQuarterChange,
  onReset,
  onSearch,
  businessUnits = [],
  clients = [],
  departments = [],
  loading = false,
  activeView = 'finance',
  onViewChange,
  showViewSelector = false,
}) => {
  const currentYear = new Date().getFullYear();
  const fiscalYears = [currentYear - 1, currentYear, currentYear + 1];
  const quarters = [1, 2, 3, 4];

  const sortedBusinessUnits = [...businessUnits].sort((a, b) => a.localeCompare(b));
  const sortedClients = [...clients].sort((a, b) => a.localeCompare(b));
  const sortedDepartments = [...departments].sort((a, b) => a.localeCompare(b));

  const getQuarterLabel = (quarter: number) => {
    const ranges: { [key: number]: string } = {
      1: 'Q1 (APR-JUN)',
      2: 'Q2 (JUL-SEP)',
      3: 'Q3 (OCT-DEC)',
      4: 'Q4 (JAN-MAR)'
    };
    return ranges[quarter];
  };

  const getFYLabel = (year: number) => {
    const nextYearShort = (year + 1).toString().slice(-2);
    return `FY ${year}-${nextYearShort}`;
  };

  const handleApplyFilters = () => {
    onSearch();
    onClose();
  };

  return (
    <Drawer
      title={
        <div className="filter-drawer-title">
          <FilterOutlined style={{ marginRight: 8 }} />
          <span>Revenue Forecast Filters</span>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={420}
      className="filter-drawer"
      closeIcon={<CloseOutlined />}
      footer={
        <div className="filter-drawer-footer">
          <Button 
            icon={<ReloadOutlined />} 
            onClick={onReset}
            size="large"
          >
            Reset
          </Button>
          <Button 
            type="primary" 
            icon={<FilterOutlined />}
            onClick={handleApplyFilters}
            loading={loading}
            size="large"
          >
            Apply Filters
          </Button>
        </div>
      }
    >
      <div className="filter-drawer-content">
        {/* View Selector */}
        {showViewSelector && (
          <>
            <div className="filter-field">
              <Text className="filter-label">View</Text>
              <Select
                value={activeView}
                onChange={onViewChange}
                style={{ width: '100%' }}
                size="large"
              >
                <Option value="finance">Finance View</Option>
                <Option value="sales">Sales View</Option>
              </Select>
            </div>
            <Divider style={{ margin: '16px 0' }} />
          </>
        )}

        {/* Conditional Filter Order based on View */}
        {activeView === 'sales' ? (
          <>
            {/* Client Filter (First for Sales View) */}
            <div className="filter-field">
              <Text className="filter-label">Client</Text>
              <Select
                mode="multiple"
                placeholder="Select Clients"
                value={selectedClient}
                onChange={onClientChange}
                style={{ width: '100%' }}
                maxTagCount="responsive"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '8px' }}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onClientChange(clients)}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Select All
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onClientChange([])}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Clear All
                      </Button>
                    </div>
                    {menu}
                  </>
                )}
              >
                {sortedClients.map((client) => (
                  <Option key={client} value={client}>
                    {client}
                  </Option>
                ))}
              </Select>
              <Text type="secondary" className="filter-helper">
                {selectedClient.length === 0 ? 'All Clients' : `${selectedClient.length} selected`}
              </Text>
            </div>

            {/* Business Unit Filter (Second for Sales View) */}
            <div className="filter-field">
              <Text className="filter-label">Business Unit</Text>
              <Select
                mode="multiple"
                placeholder="Select Business Units"
                value={selectedBU}
                onChange={onBUChange}
                style={{ width: '100%' }}
                maxTagCount="responsive"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '8px' }}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onBUChange(businessUnits)}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Select All
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onBUChange([])}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Clear All
                      </Button>
                    </div>
                    {menu}
                  </>
                )}
              >
                {sortedBusinessUnits.map((bu) => (
                  <Option key={bu} value={bu}>
                    {bu}
                  </Option>
                ))}
              </Select>
              <Text type="secondary" className="filter-helper">
                {selectedBU.length === 0 ? 'All Business Units' : `${selectedBU.length} selected`}
              </Text>
            </div>

            {/* Department Filter (Third for Sales View) */}
            <div className="filter-field">
              <Text className="filter-label">Department</Text>
              <Select
                mode="multiple"
                placeholder="Select Departments"
                value={selectedDepartment}
                onChange={onDepartmentChange}
                style={{ width: '100%' }}
                maxTagCount="responsive"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '8px' }}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onDepartmentChange(departments)}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Select All
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onDepartmentChange([])}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Clear All
                      </Button>
                    </div>
                    {menu}
                  </>
                )}
              >
                {sortedDepartments.map((dept) => (
                  <Option key={dept} value={dept}>
                    {dept}
                  </Option>
                ))}
              </Select>
              <Text type="secondary" className="filter-helper">
                {selectedDepartment.length === 0 ? 'All Departments' : `${selectedDepartment.length} selected`}
              </Text>
            </div>
          </>
        ) : (
          <>
            {/* Business Unit Filter (First for Finance View) */}
            <div className="filter-field">
              <Text className="filter-label">Business Unit</Text>
              <Select
                mode="multiple"
                placeholder="Select Business Units"
                value={selectedBU}
                onChange={onBUChange}
                style={{ width: '100%' }}
                maxTagCount="responsive"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '8px' }}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onBUChange(businessUnits)}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Select All
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onBUChange([])}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Clear All
                      </Button>
                    </div>
                    {menu}
                  </>
                )}
              >
                {sortedBusinessUnits.map((bu) => (
                  <Option key={bu} value={bu}>
                    {bu}
                  </Option>
                ))}
              </Select>
              <Text type="secondary" className="filter-helper">
                {selectedBU.length === 0 ? 'All Business Units' : `${selectedBU.length} selected`}
              </Text>
            </div>

            {/* Department Filter (Second for Finance View) */}
            <div className="filter-field">
              <Text className="filter-label">Department</Text>
              <Select
                mode="multiple"
                placeholder="Select Departments"
                value={selectedDepartment}
                onChange={onDepartmentChange}
                style={{ width: '100%' }}
                maxTagCount="responsive"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '8px' }}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onDepartmentChange(departments)}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Select All
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onDepartmentChange([])}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Clear All
                      </Button>
                    </div>
                    {menu}
                  </>
                )}
              >
                {sortedDepartments.map((dept) => (
                  <Option key={dept} value={dept}>
                    {dept}
                  </Option>
                ))}
              </Select>
              <Text type="secondary" className="filter-helper">
                {selectedDepartment.length === 0 ? 'All Departments' : `${selectedDepartment.length} selected`}
              </Text>
            </div>

            {/* Client Filter (Third for Finance View) */}
            <div className="filter-field">
              <Text className="filter-label">Client</Text>
              <Select
                mode="multiple"
                placeholder="Select Clients"
                value={selectedClient}
                onChange={onClientChange}
                style={{ width: '100%' }}
                maxTagCount="responsive"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '8px' }}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onClientChange(clients)}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Select All
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onClientChange([])}
                        style={{ padding: '4px 8px', height: 'auto' }}
                      >
                        Clear All
                      </Button>
                    </div>
                    {menu}
                  </>
                )}
              >
                {sortedClients.map((client) => (
                  <Option key={client} value={client}>
                    {client}
                  </Option>
                ))}
              </Select>
              <Text type="secondary" className="filter-helper">
                {selectedClient.length === 0 ? 'All Clients' : `${selectedClient.length} selected`}
              </Text>
            </div>
          </>
        )}

        {/* Fiscal Year and Quarter Filters */}
        <Divider style={{ margin: '24px 0' }} />

        {/* Time Period Filters */}
        <div className="filter-section">
          <div className="filter-field">
            <Text className="filter-label">Start Period</Text>
            <Row gutter={8}>
              <Col span={14}>
                <Select
                  placeholder="Fiscal Year"
                  value={startYear}
                  onChange={onStartYearChange}
                  style={{ width: '100%' }}
                >
                  {fiscalYears.map((year) => (
                    <Option key={year} value={year}>
                      {getFYLabel(year)}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={10}>
                <Select
                  placeholder="Quarter"
                  value={startQuarter}
                  onChange={onStartQuarterChange}
                  style={{ width: '100%' }}
                >
                  {quarters.map((q) => (
                    <Option key={q} value={q}>
                      {getQuarterLabel(q)}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </div>

          <div className="filter-field">
            <Text className="filter-label">End Period</Text>
            <Row gutter={8}>
              <Col span={14}>
                <Select
                  placeholder="Fiscal Year"
                  value={endYear}
                  onChange={onEndYearChange}
                  style={{ width: '100%' }}
                >
                  {fiscalYears.map((year) => (
                    <Option key={year} value={year}>
                      {getFYLabel(year)}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={10}>
                <Select
                  placeholder="Quarter"
                  value={endQuarter}
                  onChange={onEndQuarterChange}
                  style={{ width: '100%' }}
                >
                  {quarters.map((q) => (
                    <Option key={q} value={q}>
                      {getQuarterLabel(q)}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </div>

          <Text type="secondary" className="filter-helper">
            From {getFYLabel(startYear)} {getQuarterLabel(startQuarter)} to {getFYLabel(endYear)} {getQuarterLabel(endQuarter)}
          </Text>
        </div>
      </div>
    </Drawer>
  );
};

export default FilterDrawer;
